import { useCallback, useEffect, useState } from 'react';
import { groupTripApi } from '../../api/groupTripApi';
import { useTravelTranslation } from '../../app/TravelLocaleContext';
import { ConfirmDialog } from '../../components/ui';
import {
  canShowHostLateJoinActions,
  canShowLateJoinEntry,
  formatEffectiveJoinAt,
  lateJoinFailureCode,
  lateJoinFailureMessage,
  lateJoinRequestStatusLabel,
  normalizeLateJoinHostQueue,
  normalizeLateJoinRequestList,
} from './groupTripLateJoinModel';

function requestBody(row, t, tf) {
  if (row.status === 'PENDING') return t('travel.groupTrip.lateJoinPendingBody');
  if (row.status === 'CANCELLED') return t('travel.groupTrip.lateJoinCancelledBody');
  if (row.status === 'REJECTED') return t('travel.groupTrip.lateJoinRejectedBody');
  if (row.status === 'APPROVED') {
    const when = formatEffectiveJoinAt(row.effectiveJoinAt);
    return when
      ? `${t('travel.groupTrip.lateJoinApprovedBody')} ${tf('travel.groupTrip.lateJoinJoinAt', { when })}`
      : t('travel.groupTrip.lateJoinApprovedBody');
  }
  return '';
}

export default function GroupTripLateJoinSection({
  groupTripId,
  role,
  viewerId,
  hostMemberId,
  travelers = [],
  onChanged,
}) {
  const { t, tf } = useTravelTranslation();
  const isHost = role === 'host' && canShowHostLateJoinActions(viewerId, hostMemberId);
  const showEntry = role === 'candidate' && canShowLateJoinEntry({ viewerId, hostMemberId, travelers });
  const [phase, setPhase] = useState('loading');
  const [items, setItems] = useState([]);
  const [capacity, setCapacity] = useState(null);
  const [occupied, setOccupied] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [busy, setBusy] = useState('');
  const [notice, setNotice] = useState('');
  const [banner, setBanner] = useState('');
  const [confirm, setConfirm] = useState(null);

  const reload = useCallback(async () => {
    if (isHost) {
      if (typeof groupTripApi.listLateJoinRequests !== 'function') return null;
      const queue = normalizeLateJoinHostQueue(
        await groupTripApi.listLateJoinRequests(groupTripId, 'PENDING'),
      );
      setCapacity(queue.capacity);
      setOccupied(queue.occupied);
      setItems(queue.items);
      setSelectedId((current) => (
        queue.items.some((item) => String(item.requestId) === String(current))
          ? current
          : queue.items[0]?.requestId || null
      ));
      return queue.items;
    }
    if (role === 'candidate') {
      if (typeof groupTripApi.listMyLateJoinRequests !== 'function') return null;
      const next = normalizeLateJoinRequestList(await groupTripApi.listMyLateJoinRequests(groupTripId));
      setItems(next);
      return next;
    }
    return null;
  }, [groupTripId, isHost, role]);

  useEffect(() => {
    if (!groupTripId || (role === 'host' && !isHost)) {
      setPhase('loaded');
      setItems([]);
      return undefined;
    }
    if (role === 'candidate' && typeof groupTripApi.listMyLateJoinRequests !== 'function') {
      setPhase('loaded');
      return undefined;
    }
    if (isHost && typeof groupTripApi.listLateJoinRequests !== 'function') {
      setPhase('loaded');
      return undefined;
    }
    let cancelled = false;
    setPhase('loading');
    reload()
      .then(() => {
        if (!cancelled) setPhase('loaded');
      })
      .catch(() => {
        if (!cancelled) {
          setItems([]);
          setPhase('error');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [groupTripId, isHost, reload, role]);

  async function run(kind, action) {
    if (busy) return;
    setBusy(kind);
    setNotice('');
    setBanner('');
    try {
      await action();
      await reload();
      if (typeof onChanged === 'function') await onChanged();
      setConfirm(null);
      setPhase('loaded');
    } catch (cause) {
      const code = lateJoinFailureCode(cause);
      if (code === 'DUPLICATE_PENDING_REQUEST') {
        await reload();
        setNotice(t('travel.groupTrip.lateJoinSamePending'));
        setConfirm(null);
      } else if (code === 'CAPACITY_FULL') {
        await reload();
        setBanner(t('travel.groupTrip.lateJoinCapacityFullBody'));
        setConfirm(null);
      } else if (code === 'REQUEST_NOT_PENDING') {
        await reload();
        setNotice(lateJoinFailureMessage(cause, t));
        setConfirm(null);
      } else if (code === 'APPROVED_WITHOUT_TRAVELER') {
        await reload();
        setNotice(lateJoinFailureMessage(cause, t));
        setConfirm(null);
      } else {
        setNotice(lateJoinFailureMessage(cause, t) || t('travel.groupTrip.lateJoinLoadError'));
      }
      setPhase('loaded');
    } finally {
      setBusy('');
    }
  }

  if (role === 'host' && (!isHost || typeof groupTripApi.listLateJoinRequests !== 'function')) return null;
  if (role === 'candidate' && typeof groupTripApi.listMyLateJoinRequests !== 'function') return null;
  if (role === 'candidate' && viewerId == null) return null;

  const selected = items.find((item) => String(item.requestId) === String(selectedId)) || null;
  const confirmCopy = {
    'candidate-cancel': {
      title: t('travel.groupTrip.lateJoinCancelTitle'),
      description: t('travel.groupTrip.lateJoinCancelBody'),
      confirmLabel: t('travel.groupTrip.lateJoinCancelConfirm'),
      danger: false,
    },
    'host-cancel': {
      title: t('travel.groupTrip.lateJoinHostCancelTitle'),
      description: t('travel.groupTrip.lateJoinHostCancelBody'),
      confirmLabel: t('travel.groupTrip.lateJoinHostCancel'),
      danger: false,
    },
    reject: {
      title: t('travel.groupTrip.lateJoinRejectTitle'),
      description: t('travel.groupTrip.lateJoinRejectBody'),
      confirmLabel: t('travel.groupTrip.lateJoinReject'),
      danger: true,
    },
    approve: {
      title: t('travel.groupTrip.lateJoinApproveTitle'),
      description: t('travel.groupTrip.lateJoinApproveBody'),
      confirmLabel: t('travel.groupTrip.lateJoinApprove'),
      danger: false,
    },
  }[confirm?.kind] || null;

  return (
    <section
      className={`group-trips__panel group-trips__late-join${isHost ? ' group-trips__late-join--host' : ''}`}
      data-testid={isHost ? 'late-join-host' : 'late-join-candidate'}
    >
      <h2>{t('travel.groupTrip.lateJoinTitle')}</h2>
      {phase === 'loading' ? <p className="group-trips__hint">{t('travel.groupTrip.lateJoinLoading')}</p> : null}
      {phase === 'error' ? (
        <p role="alert">
          {t('travel.groupTrip.lateJoinLoadError')}
          {' '}
          <button type="button" onClick={() => reload().then(() => setPhase('loaded')).catch(() => setPhase('error'))}>
            {t('travel.groupTrip.lateJoinRetry')}
          </button>
        </p>
      ) : null}
      {banner ? (
        <p role="status" data-testid="late-join-capacity-full">
          <strong>{t('travel.groupTrip.lateJoinCapacityFullTitle')}</strong>
          {' '}
          {banner}
        </p>
      ) : null}
      {notice ? <p role="status" data-testid="late-join-notice">{notice}</p> : null}
      {role === 'candidate' && phase === 'loaded' ? (
        <>
          {showEntry ? (
            <div data-testid="late-join-entry">
              <p>{t('travel.groupTrip.lateJoinEntryBody')}</p>
              <button
                type="button"
                disabled={Boolean(busy)}
                onClick={() => {
                  const hadPending = items.some((row) => row.status === 'PENDING');
                  run('requesting', async () => {
                    await groupTripApi.requestLateJoin(groupTripId);
                    if (hadPending) setNotice(t('travel.groupTrip.lateJoinSamePending'));
                  });
                }}
              >
                {busy === 'requesting' ? t('travel.groupTrip.lateJoinRequesting') : t('travel.groupTrip.lateJoinRequest')}
              </button>
            </div>
          ) : null}
          {items.length === 0 ? (
            <div data-testid="late-join-empty">
              <p>{t('travel.groupTrip.lateJoinEmptyTitle')}</p>
              <p className="group-trips__hint">{t('travel.groupTrip.lateJoinEmptyBody')}</p>
            </div>
          ) : (
            <ol className="group-trips__list" data-testid="late-join-history">
              {items.map((row) => (
                <li className="group-trips__card" key={row.requestId} data-testid={`late-join-request-${row.requestId}`}>
                  <p>
                    <span data-testid={`late-join-status-${row.requestId}`}>{lateJoinRequestStatusLabel(row.status, t)}</span>
                  </p>
                  <p>{requestBody(row, t, tf)}</p>
                  {row.status === 'PENDING' ? (
                    <p className="group-trips__hint">{t('travel.groupTrip.lateJoinPendingCapacity')}</p>
                  ) : null}
                  {row.status === 'PENDING' ? (
                    <button
                      type="button"
                      disabled={Boolean(busy)}
                      onClick={() => setConfirm({ kind: 'candidate-cancel', requestId: row.requestId })}
                    >
                      {busy === 'cancelling' ? t('travel.groupTrip.lateJoinCancelling') : t('travel.groupTrip.lateJoinCancelAction')}
                    </button>
                  ) : null}
                  {showEntry && !items.some((item) => item.status === 'PENDING') && (row.status === 'REJECTED' || row.status === 'CANCELLED') ? (
                    <button
                      type="button"
                      disabled={Boolean(busy)}
                      onClick={() => run('requesting', () => groupTripApi.requestLateJoin(groupTripId))}
                    >
                      {t('travel.groupTrip.lateJoinResubmit')}
                    </button>
                  ) : null}
                </li>
              ))}
            </ol>
          )}
        </>
      ) : null}
      {isHost && phase === 'loaded' ? (
        <div className="group-trips__late-join-layout">
          <div>
            {capacity != null && occupied != null ? (
              <p data-testid="late-join-capacity">
                {tf('travel.groupTrip.lateJoinHostCount', { occupied, capacity })}
                {' '}
                {t('travel.groupTrip.lateJoinHostCapacity')}
              </p>
            ) : null}
            {items.length === 0 ? (
              <p data-testid="late-join-host-empty">{t('travel.groupTrip.lateJoinHostEmpty')}</p>
            ) : (
              <ul className="group-trips__list" data-testid="late-join-host-queue">
                {items.map((row) => (
                  <li className="group-trips__card" key={row.requestId}>
                    <button
                      type="button"
                      aria-pressed={String(selectedId) === String(row.requestId)}
                      onClick={() => setSelectedId(row.requestId)}
                    >
                      {lateJoinRequestStatusLabel(row.status, t)}
                      {row.createTime ? ` · ${formatEffectiveJoinAt(row.createTime) || row.createTime}` : ''}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          {selected ? (
            <div data-testid="late-join-host-detail">
              <p>
                <span data-testid="late-join-host-status">{lateJoinRequestStatusLabel(selected.status, t)}</span>
              </p>
              <p>{t('travel.groupTrip.lateJoinPendingBody')}</p>
              <div className="group-trips__actions group-trips__late-join-actions">
                <button
                  type="button"
                  className="group-trips__apply-primary"
                  disabled={Boolean(busy)}
                  onClick={() => setConfirm({ kind: 'approve', requestId: selected.requestId })}
                >
                  {busy === 'approving' ? t('travel.groupTrip.lateJoinApproving') : t('travel.groupTrip.lateJoinApprove')}
                </button>
                <button
                  type="button"
                  className="group-trips__late-join-reject"
                  disabled={Boolean(busy)}
                  aria-describedby="late-join-reject-meaning"
                  onClick={() => setConfirm({ kind: 'reject', requestId: selected.requestId })}
                >
                  {busy === 'rejecting' ? t('travel.groupTrip.lateJoinRejecting') : t('travel.groupTrip.lateJoinReject')}
                </button>
                <button
                  type="button"
                  disabled={Boolean(busy)}
                  onClick={() => setConfirm({ kind: 'host-cancel', requestId: selected.requestId })}
                >
                  {busy === 'cancelling' ? t('travel.groupTrip.lateJoinCancelling') : t('travel.groupTrip.lateJoinHostCancel')}
                </button>
              </div>
              <p id="late-join-reject-meaning" className="group-trips__hint">{t('travel.groupTrip.lateJoinRejectMeaning')}</p>
            </div>
          ) : null}
        </div>
      ) : null}
      <ConfirmDialog
        open={Boolean(confirmCopy)}
        title={confirmCopy?.title || ''}
        description={confirmCopy?.description || ''}
        confirmLabel={confirmCopy?.confirmLabel || ''}
        cancelLabel={confirm?.kind === 'approve' ? t('travel.groupTrip.lateJoinBack') : t('travel.groupTrip.lateJoinNotYet')}
        danger={Boolean(confirmCopy?.danger)}
        loading={Boolean(busy)}
        onCancel={() => {
          if (!busy) setConfirm(null);
        }}
        onConfirm={() => {
          const requestId = confirm?.requestId;
          if (!requestId) return;
          if (confirm.kind === 'candidate-cancel' || confirm.kind === 'host-cancel') {
            run('cancelling', () => groupTripApi.cancelLateJoinRequest(groupTripId, requestId));
          } else if (confirm.kind === 'reject') {
            run('rejecting', () => groupTripApi.rejectLateJoinRequest(groupTripId, requestId));
          } else if (confirm.kind === 'approve') {
            run('approving', () => groupTripApi.approveLateJoinRequest(groupTripId, requestId));
          }
        }}
      />
    </section>
  );
}
